import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useEffect, useRef } from "react";
import { useTenant } from "../lib/tenant-context";

// Types
interface Program {
  id: string;
  name: string;
  type: "takeback" | "trade_in" | "resale";
  status: "draft" | "active" | "archived";
  completeness: number;
  config: ProgramConfig;
  created_at: string;
  updated_at: string;
}

interface ProgramConfig {
  // Basic info
  name?: string;
  description?: string;

  // Eligibility
  eligible_products?: {
    brands?: string[];
    categories?: string[];
    any_product?: boolean;
  };

  // Condition requirements
  conditions?: {
    accepted_grades?: string[];
    minimum_grade?: string;
    rejection_criteria?: string[];
  };

  // Collection/Return method
  collection?: {
    mail_in?: {
      enabled: boolean;
      prepaid_label?: boolean;
      carrier?: string;
    };
    in_store_drop_off?: {
      enabled: boolean;
      stores?: string[];
    };
    courier_pickup?: {
      enabled: boolean;
      min_value?: number;
    };
  };

  // Valuation & Payouts
  valuation?: {
    method?: "fixed" | "percentage" | "condition_based" | "market_based";
    fixed_amount?: number;
    percentage_of_retail?: number;
    condition_multipliers?: {
      excellent?: number;
      good?: number;
      fair?: number;
      poor?: number;
    };
  };

  // Payout options
  payout?: {
    store_credit?: { enabled: boolean; bonus_percentage?: number };
    cash?: { enabled: boolean };
    gift_card?: { enabled: boolean };
    charity_donation?: { enabled: boolean; charity_name?: string };
  };

  // Processing
  processing?: {
    quote_validity_days?: number;
    inspection_required?: boolean;
    estimated_days?: number;
  };

  // Routing (what happens to items) - Trade-in specific
  routing?: {
    resell?: { enabled: boolean; conditions?: string[] };
    refurbish?: { enabled: boolean; conditions?: string[] };
    donate?: { enabled: boolean; partner?: string };
    recycle?: { enabled: boolean; is_default?: boolean };
    priority_order?: string[];
  };

  // Takeback-specific config
  takeback?: {
    incentive?: "voucher" | "store_credit" | "charity_donation" | "none";
    voucher_amount?: number;
    destination?: "recycle" | "donate" | "refurbish_donate" | "mix";
    recycling_partner?: string;
    charity_partner?: string;
  };

  // Resale-specific config
  resale?: {
    source?: "trade_in" | "consignment" | "external" | "mixed";
    quality_threshold?: "A" | "B" | "C";
    channels?: string[];
    pricing_strategy?: "percentage" | "market_based" | "manual" | "auction";
    percentage?: number;
    commission_rate?: number;
    fulfillment?: "direct" | "pickup" | "dropship" | "mixed";
  };
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  content: string;
  suggestions?: string[];
  field?: string;
  examples?: string;
  tip?: string;
  timestamp: Date;
}

// Completeness weights: name=10, eligible_products=15, conditions=15, collection=20, valuation=20, payout=15, routing=5

function calculateCompleteness(
  config: ProgramConfig,
  programType: "trade_in" | "takeback" | "resale" = "trade_in",
): { score: number; missing: string[] } {
  const missing: string[] = [];
  let score = 0;

  // Name - required for all types
  if (config.name?.trim()) score += 15;
  else missing.push("Program name");

  if (programType === "resale") {
    // RESALE PROGRAM completeness
    if (config.resale?.source) score += 17;
    else missing.push("Inventory source");

    if (config.resale?.quality_threshold) score += 17;
    else missing.push("Quality standards");

    if (config.resale?.channels?.length) score += 17;
    else missing.push("Sales channels");

    if (config.resale?.pricing_strategy) score += 17;
    else missing.push("Pricing strategy");

    if (config.resale?.fulfillment) score += 17;
    else missing.push("Fulfillment method");
  } else if (programType === "takeback") {
    // TAKEBACK PROGRAM completeness
    if (
      config.eligible_products?.any_product ||
      config.eligible_products?.brands?.length
    ) {
      score += 17;
    } else {
      missing.push("What products to accept");
    }

    if (config.conditions?.minimum_grade) score += 17;
    else missing.push("Condition requirements");

    const hasCollection =
      config.collection?.mail_in?.enabled ||
      config.collection?.in_store_drop_off?.enabled;
    if (hasCollection) score += 17;
    else missing.push("Collection method");

    if (config.takeback?.incentive) score += 17;
    else missing.push("Customer incentive");

    if (config.takeback?.destination) score += 17;
    else missing.push("Item destination");
  } else {
    // TRADE-IN PROGRAM completeness (default)
    if (
      config.eligible_products?.any_product ||
      config.eligible_products?.brands?.length ||
      config.eligible_products?.categories?.length
    ) {
      score += 14;
    } else {
      missing.push("Which products are eligible");
    }

    if (
      config.conditions?.accepted_grades?.length ||
      config.conditions?.minimum_grade
    ) {
      score += 14;
    } else {
      missing.push("What condition items need to be in");
    }

    const hasCollection =
      config.collection?.mail_in?.enabled ||
      config.collection?.in_store_drop_off?.enabled ||
      config.collection?.courier_pickup?.enabled;
    if (hasCollection) score += 14;
    else missing.push("How customers return items");

    if (config.valuation?.method) score += 14;
    else missing.push("How much to pay customers");

    const hasPayout =
      config.payout?.store_credit?.enabled ||
      config.payout?.cash?.enabled ||
      config.payout?.gift_card?.enabled ||
      config.payout?.charity_donation?.enabled;
    if (hasPayout) score += 14;
    else missing.push("How customers receive payment");

    const hasRouting =
      config.routing?.resell?.enabled ||
      config.routing?.refurbish?.enabled ||
      config.routing?.donate?.enabled ||
      config.routing?.recycle?.enabled;
    if (hasRouting) score += 15;
    else missing.push("What happens to items");
  }

  return { score: Math.min(100, score), missing };
}

// Program sections for structured editing - varies by type
const PROGRAM_SECTIONS_BY_TYPE = {
  trade_in: [
    { id: "basics", label: "Program Basics", fields: ["name"] },
    {
      id: "eligibility",
      label: "Eligibility",
      fields: ["eligible_products", "conditions"],
    },
    {
      id: "collection",
      label: "Collection",
      fields: ["collection", "mail_prepaid"],
    },
    {
      id: "valuation",
      label: "Valuation",
      fields: ["valuation", "valuation_condition"],
    },
    {
      id: "payout",
      label: "Customer Payout",
      fields: ["payout_method", "store_credit_bonus"],
    },
    {
      id: "routing",
      label: "Item Routing",
      fields: ["routing", "routing_priority"],
    },
  ],
  takeback: [
    { id: "basics", label: "Program Basics", fields: ["name"] },
    {
      id: "eligibility",
      label: "What to Accept",
      fields: ["eligible_products", "conditions"],
    },
    {
      id: "collection",
      label: "Collection",
      fields: ["collection", "mail_prepaid"],
    },
    {
      id: "incentive",
      label: "Customer Incentive",
      fields: ["takeback_incentive"],
    },
    {
      id: "destination",
      label: "Item Destination",
      fields: ["takeback_destination"],
    },
  ],
  resale: [
    { id: "basics", label: "Program Basics", fields: ["name"] },
    { id: "inventory", label: "Inventory Source", fields: ["resale_source"] },
    {
      id: "quality",
      label: "Quality Standards",
      fields: ["resale_conditions"],
    },
    { id: "channels", label: "Sales Channels", fields: ["resale_channels"] },
    { id: "pricing", label: "Pricing Strategy", fields: ["resale_pricing"] },
    { id: "fulfillment", label: "Fulfillment", fields: ["resale_fulfillment"] },
  ],
};

// Legacy alias for compatibility
const PROGRAM_SECTIONS = PROGRAM_SECTIONS_BY_TYPE.trade_in;

// AI prompt templates for guided creation - with better examples and context
const GUIDED_PROMPTS: Record<
  string,
  {
    question: string;
    suggestions?: string[];
    examples?: string;
    tip?: string;
  }
> = {
  name: {
    question:
      "First, let's name your program. Keep it short and customer-friendly.",
    suggestions: ["Lululemon Like New", "Trade-In Rewards", "Circular Program"],
    examples:
      "Examples: 'Nike Refurbished', 'Patagonia Worn Wear', 'IKEA Buyback'",
    tip: "This name will be shown to customers, so keep it simple and memorable.",
  },
  eligible_products: {
    question: "Which products can customers trade in?",
    suggestions: [
      "Only our brand",
      "Any brand, specific categories",
      "Any product",
    ],
    examples:
      "• **Only our brand** - Accept only products you manufactured\n• **Specific categories** - e.g., 'Athletic wear from any brand'\n• **Any product** - Accept anything in your category",
    tip: "Starting with your own brand is easier to manage and authenticate.",
  },
  conditions: {
    question:
      "What condition must items be in? This affects both what you accept and what you pay.",
    suggestions: ["Like new only", "Good or better", "Any condition"],
    examples:
      "• **Like new** (A grade) - Unworn/unused, tags may be removed\n• **Good** (B grade) - Gently used, minor signs of wear\n• **Any condition** (C grade) - Visible wear, may need repair",
    tip: "Accepting any condition = more volume but lower resale value. Like new = higher margins but fewer submissions.",
  },
  collection: {
    question: "How will customers return items to you?",
    suggestions: ["Mail-in only", "In-store only", "Both options"],
    examples:
      "• **Mail-in** - Wider reach, requires shipping logistics\n• **In-store** - Instant assessment, no shipping costs\n• **Both** - Maximum convenience, more complex operations",
    tip: "If you have retail stores, offering both options increases participation by 40%.",
  },
  mail_prepaid: {
    question: "For mail-in returns, who pays shipping?",
    suggestions: [
      "We pay (prepaid label)",
      "Customer pays",
      "Free over £30 value",
    ],
    examples:
      "• **Prepaid labels** - Higher participation, ~£3-5 cost per return\n• **Customer pays** - Lower volume but pre-qualified submissions\n• **Threshold** - Balance between volume and cost",
    tip: "Prepaid labels increase participation by 60% but add £3-5 per item in costs.",
  },
  valuation: {
    question: "How should we calculate what to pay customers?",
    suggestions: ["Fixed amount", "% of retail price", "Based on condition"],
    examples:
      "• **Fixed** - Simple, e.g., '£10 for any item' - easy to communicate\n• **Percentage** - e.g., '30% of retail price' - scales with item value\n• **Condition-based** - Different rates per grade - most accurate",
    tip: "Condition-based is most fair but requires assessment. Fixed is simplest to launch with.",
  },
  valuation_condition: {
    question: "Set the payout for each condition grade:",
    suggestions: [
      "A: £30, B: £20, C: £10",
      "A: 40%, B: 25%, C: 10%",
      "A: £50, B: £30, C: £15",
    ],
    examples:
      "**Typical ranges:**\n• Grade A (Like New): 30-50% of retail\n• Grade B (Good): 15-30% of retail\n• Grade C (Fair): 5-15% of retail",
    tip: "Your margins on resale should be 40-60%. Price payouts accordingly.",
  },
  payout_method: {
    question: "How do you want to pay customers?",
    suggestions: [
      "Store credit",
      "Cash/PayPal",
      "Gift card",
      "Charity donation",
    ],
    examples:
      "• **Store credit** - 70% of brands choose this, drives repeat purchases\n• **Cash** - Attracts more customers but no guaranteed return\n• **Gift card** - Flexible, can add bonus value\n• **Charity** - Good for low-value items, positive PR",
    tip: "Store credit with a bonus (e.g., +20%) is the most profitable option.",
  },
  store_credit_bonus: {
    question: "Offer a bonus for choosing store credit?",
    suggestions: ["+20% bonus", "+10% bonus", "No bonus"],
    examples:
      "A 20% bonus on a £20 payout = £24 store credit.\nThis typically increases store credit selection from 40% to 85%.",
    tip: "The bonus costs you less than the repeat purchase it generates.",
  },
  routing: {
    question: "What happens to items after you collect them?",
    suggestions: [
      "Resell as-is",
      "Refurbish then resell",
      "Recycle all",
      "Mixed routing",
    ],
    examples:
      "• **Resell** - Grade A/B items sold on your site or marketplace\n• **Refurbish** - Repair/clean items to increase value\n• **Recycle** - Material recovery for items that can't be sold\n• **Mixed** - Route each item based on condition",
    tip: "Most successful programs use mixed routing: Resell best items, recycle the rest.",
  },
  routing_priority: {
    question: "Set the priority order for routing items:",
    suggestions: [
      "Resell → Recycle",
      "Resell → Refurbish → Recycle",
      "Resell → Donate → Recycle",
    ],
    examples:
      "Items flow through channels in order until placed:\n1. **Resell** - If condition meets threshold\n2. **Refurbish** - If repairable and profitable\n3. **Donate** - If usable but not resellable\n4. **Recycle** - Catch-all for remaining items",
    tip: "Always have 'Recycle' as the final catch-all to ensure every item has a destination.",
  },

  // ============ TAKEBACK-SPECIFIC PROMPTS ============
  takeback_incentive: {
    question: "What incentive do customers get for returning items?",
    suggestions: [
      "Discount voucher",
      "Store credit",
      "Charity donation",
      "Nothing - feel good factor",
    ],
    examples:
      "• **Voucher** - e.g., '£5 off next purchase' - drives repeat visits\n• **Store credit** - More value, encourages larger purchases\n• **Charity** - Donate value to partner charity\n• **None** - Some customers just want to recycle responsibly",
    tip: "Even a small voucher (£5) significantly increases participation.",
  },
  takeback_destination: {
    question: "What happens to items you collect?",
    suggestions: [
      "Recycle materials",
      "Donate to charity",
      "Refurbish and donate",
      "Mix based on condition",
    ],
    examples:
      "• **Recycle** - Extract materials, partner with textile recycler\n• **Donate** - Partner with charities for usable items\n• **Refurbish** - Repair items before donating\n• **Mix** - Best condition donated, rest recycled",
    tip: "Partner with a certified textile recycler for sustainability credentials.",
  },

  // ============ RESALE-SPECIFIC PROMPTS ============
  resale_source: {
    question: "Where does your resale inventory come from?",
    suggestions: [
      "Our trade-in program",
      "Consignment from customers",
      "We source externally",
      "Mix of sources",
    ],
    examples:
      "• **Trade-in** - Items from your own takeback program\n• **Consignment** - Customers list items, you sell for commission\n• **External** - You buy inventory from wholesalers/auctions\n• **Mix** - Multiple inventory sources",
    tip: "Connecting to your trade-in program creates a circular economy.",
  },
  resale_conditions: {
    question: "What quality standards must items meet to be listed?",
    suggestions: ["Like new only", "Good or better", "Any sellable condition"],
    examples:
      "• **Like new** - Highest prices, but limited inventory\n• **Good** - Balance of volume and value\n• **Any sellable** - Maximum inventory, requires good grading",
    tip: "Clear quality descriptions reduce returns and build trust.",
  },
  resale_channels: {
    question: "Where will you sell resale items?",
    suggestions: [
      "Our website only",
      "Telegram store",
      "Marketplaces (eBay, Depop)",
      "All channels",
    ],
    examples:
      "• **Own site** - Full control, higher margins\n• **Telegram** - Direct customer relationship\n• **Marketplaces** - Wider reach, fees apply\n• **All** - Maximum exposure",
    tip: "Start with one channel, expand once you have the operations down.",
  },
  resale_pricing: {
    question: "How will you price resale items?",
    suggestions: [
      "Fixed % of retail",
      "Market-based pricing",
      "Manual pricing",
      "Auction",
    ],
    examples:
      "• **% of retail** - e.g., 40-60% of original price\n• **Market-based** - AI suggests based on current listings\n• **Manual** - You set each price\n• **Auction** - Customers bid, highest wins",
    tip: "Market-based pricing typically achieves 15% higher sell-through.",
  },
  resale_fulfillment: {
    question: "How will orders be fulfilled?",
    suggestions: [
      "We ship directly",
      "Customer pickup",
      "Drop-ship from consignor",
      "Mix",
    ],
    examples:
      "• **Direct ship** - You hold inventory, ship from warehouse\n• **Pickup** - Customers collect from store\n• **Drop-ship** - Consignor ships directly to buyer\n• **Mix** - Depends on item source",
    tip: "Direct shipping gives you quality control over the unboxing experience.",
  },
};

// Tenant-specific demo programs
function getTenantDemoPrograms(tenantId: string): Program[] {
  if (tenantId === "arcteryx") {
    return [
      {
        id: "prg-arc-1",
        name: "Arc'teryx ReBIRD",
        type: "trade_in",
        status: "active",
        completeness: 100,
        config: {
          name: "Arc'teryx ReBIRD Trade-In",
          description: "Trade in your used Arc'teryx gear for store credit",
          eligible_products: { brands: ["Arc'teryx"], any_product: false },
          conditions: {
            minimum_grade: "C",
            accepted_grades: ["A", "B+", "B", "B-", "C+", "C"],
          },
          collection: {
            mail_in: { enabled: true, prepaid_label: true, carrier: "UPS" },
            in_store_drop_off: {
              enabled: true,
              stores: ["Vancouver", "Seattle", "NYC"],
            },
          },
          valuation: {
            method: "condition_based",
            condition_multipliers: {
              excellent: 0.45,
              good: 0.3,
              fair: 0.15,
              poor: 0.05,
            },
          },
          payout: {
            store_credit: { enabled: true, bonus_percentage: 20 },
          },
          routing: {
            resell: { enabled: true, conditions: ["A", "B+"] },
            refurbish: { enabled: true, conditions: ["B", "B-"] },
            recycle: { enabled: true, is_default: true },
            priority_order: ["resell", "refurbish", "recycle"],
          },
        },
        created_at: "2024-01-15T10:00:00Z",
        updated_at: "2024-12-01T15:30:00Z",
      },
      {
        id: "prg-arc-2",
        name: "Arc'teryx Repair Program",
        type: "takeback",
        status: "draft",
        completeness: 65,
        config: {
          name: "Arc'teryx Repair & Return",
          description: "Send in damaged gear for professional repair",
          eligible_products: { brands: ["Arc'teryx"], any_product: false },
          conditions: { minimum_grade: "D" },
          collection: { mail_in: { enabled: true, prepaid_label: true } },
          takeback: { incentive: "voucher", voucher_amount: 25 },
        },
        created_at: "2024-06-01T10:00:00Z",
        updated_at: "2024-11-15T09:00:00Z",
      },
    ];
  } else if (tenantId === "nespresso") {
    return [
      {
        id: "prg-nes-1",
        name: "Nespresso Second Life",
        type: "takeback",
        status: "active",
        completeness: 100,
        config: {
          name: "Nespresso Capsule Recycling",
          description:
            "Recycle your used Nespresso capsules and earn loyalty points",
          eligible_products: { any_product: true },
          conditions: { minimum_grade: "any" },
          collection: {
            mail_in: { enabled: true, prepaid_label: true },
            in_store_drop_off: { enabled: true },
          },
          takeback: {
            incentive: "store_credit",
            destination: "recycle",
            recycling_partner: "TerraCycle",
          },
        },
        created_at: "2024-01-01T10:00:00Z",
        updated_at: "2024-12-10T12:00:00Z",
      },
    ];
  } else if (tenantId === "lego") {
    return [
      {
        id: "prg-lego-1",
        name: "LEGO Replay",
        type: "takeback",
        status: "active",
        completeness: 100,
        config: {
          name: "LEGO Replay Donation",
          description:
            "Donate your gently used LEGO bricks to be cleaned and given to children in need",
          eligible_products: { brands: ["LEGO"], any_product: true },
          conditions: { minimum_grade: "C" },
          collection: {
            mail_in: { enabled: true, prepaid_label: true, carrier: "USPS" },
            in_store_drop_off: { enabled: true },
          },
          takeback: {
            incentive: "charity_donation",
            destination: "donate",
            charity_partner: "Give Back Box",
          },
        },
        created_at: "2024-01-01T10:00:00Z",
        updated_at: "2024-12-15T10:00:00Z",
      },
      {
        id: "prg-lego-2",
        name: "LEGO Brick Buyback",
        type: "trade_in",
        status: "active",
        completeness: 95,
        config: {
          name: "LEGO Brick Buyback",
          description:
            "Trade in your used LEGO bricks and complete sets for store credit",
          eligible_products: { brands: ["LEGO"], any_product: false },
          conditions: { minimum_grade: "B", accepted_grades: ["A", "B+", "B"] },
          collection: {
            mail_in: { enabled: true, prepaid_label: true },
            in_store_drop_off: {
              enabled: true,
              stores: ["All LEGO Stores", "Certified Partners"],
            },
          },
          valuation: {
            method: "condition_based",
            condition_multipliers: {
              excellent: 0.5,
              good: 0.35,
              fair: 0.2,
              poor: 0.1,
            },
          },
          payout: {
            store_credit: { enabled: true, bonus_percentage: 15 },
          },
          routing: {
            resell: {
              enabled: true,
              conditions: ["Complete sets", "Rare minifigs"],
            },
            refurbish: { enabled: true, conditions: ["Bulk bricks"] },
            recycle: { enabled: false },
            priority_order: ["resell", "refurbish"],
          },
        },
        created_at: "2024-02-15T10:00:00Z",
        updated_at: "2024-12-10T14:00:00Z",
      },
      {
        id: "prg-lego-3",
        name: "LEGO Certified Pre-Owned",
        type: "resale",
        status: "active",
        completeness: 90,
        config: {
          name: "LEGO Certified Pre-Owned",
          description:
            "Sell your complete LEGO sets through our certified resale marketplace",
          eligible_products: {
            brands: ["LEGO"],
            categories: ["Complete Sets"],
          },
          conditions: { minimum_grade: "A", accepted_grades: ["A", "A+"] },
          resale: {
            source: "trade_in",
            quality_threshold: "A",
            channels: ["LEGO.com", "BrickLink"],
            pricing_strategy: "market_based",
            commission_rate: 15,
            fulfillment: "direct",
          },
        },
        created_at: "2024-03-01T10:00:00Z",
        updated_at: "2024-12-01T09:00:00Z",
      },
    ];
  } else if (tenantId === "nike") {
    return [
      {
        id: "prg-nike-1",
        name: "Nike Refurbished",
        type: "trade_in",
        status: "active",
        completeness: 100,
        config: {
          name: "Nike Refurbished Trade-In",
          description:
            "Trade in your gently used Nike footwear for store credit",
          eligible_products: { brands: ["Nike", "Jordan"], any_product: false },
          conditions: {
            minimum_grade: "C",
            accepted_grades: ["A", "B+", "B", "B-", "C+", "C"],
          },
          collection: {
            mail_in: { enabled: true, prepaid_label: true, carrier: "UPS" },
            in_store_drop_off: {
              enabled: true,
              stores: [
                "House of Innovation NYC",
                "Nike SoHo",
                "Nike The Grove",
              ],
            },
          },
          valuation: {
            method: "condition_based",
            condition_multipliers: {
              excellent: 0.45,
              good: 0.3,
              fair: 0.15,
              poor: 0.05,
            },
          },
          payout: {
            store_credit: { enabled: true, bonus_percentage: 5 },
          },
          routing: {
            resell: { enabled: true, conditions: ["A", "B+"] },
            refurbish: { enabled: true, conditions: ["B", "B-", "C+"] },
            recycle: { enabled: true, is_default: true },
            priority_order: ["resell", "refurbish", "recycle"],
          },
        },
        created_at: "2024-01-15T10:00:00Z",
        updated_at: "2024-12-01T15:30:00Z",
      },
      {
        id: "prg-nike-2",
        name: "Jordan Collection Buyback",
        type: "trade_in",
        status: "active",
        completeness: 95,
        config: {
          name: "Jordan Collection Buyback",
          description: "Premium trade-in program for Air Jordan sneakers",
          eligible_products: {
            brands: ["Jordan"],
            categories: ["Retro", "OG"],
          },
          conditions: { minimum_grade: "B", accepted_grades: ["A", "B+", "B"] },
          collection: {
            mail_in: { enabled: true, prepaid_label: true, carrier: "UPS" },
            in_store_drop_off: { enabled: true },
          },
          valuation: {
            method: "condition_based",
            condition_multipliers: { excellent: 0.5, good: 0.35, fair: 0.2 },
          },
          payout: {
            store_credit: { enabled: true, bonus_percentage: 10 },
          },
          routing: {
            resell: { enabled: true, conditions: ["A", "B+"] },
            refurbish: { enabled: true, conditions: ["B"] },
            priority_order: ["resell", "refurbish"],
          },
        },
        created_at: "2024-03-01T10:00:00Z",
        updated_at: "2024-12-10T14:00:00Z",
      },
      {
        id: "prg-nike-3",
        name: "Nike Running Trade-Up",
        type: "trade_in",
        status: "draft",
        completeness: 70,
        config: {
          name: "Nike Running Trade-Up",
          description:
            "Trade in old running shoes for credit toward new performance footwear",
          eligible_products: {
            brands: ["Nike"],
            categories: ["Running", "Performance"],
          },
          conditions: { minimum_grade: "C" },
          collection: {
            mail_in: { enabled: true, prepaid_label: true },
            in_store_drop_off: { enabled: true },
          },
          valuation: {
            method: "condition_based",
            condition_multipliers: { excellent: 0.35, good: 0.25, fair: 0.15 },
          },
          payout: {
            store_credit: { enabled: true, bonus_percentage: 15 },
          },
        },
        created_at: "2024-06-01T10:00:00Z",
        updated_at: "2024-11-15T09:00:00Z",
      },
    ];
  }

  // Default: Lululemon
  return [
    {
      id: "prg-lulu-1",
      name: "lululemon Like New",
      type: "trade_in",
      status: "active",
      completeness: 85,
      config: {
        name: "lululemon Like New",
        description: "Trade in your gently used lululemon for store credit",
        eligible_products: { brands: ["lululemon"], any_product: false },
        conditions: { minimum_grade: "B", accepted_grades: ["A", "B+", "B"] },
        collection: {
          mail_in: { enabled: true, prepaid_label: true },
          in_store_drop_off: { enabled: true },
        },
        valuation: { method: "percentage", percentage_of_retail: 30 },
        payout: { store_credit: { enabled: true, bonus_percentage: 10 } },
        routing: {
          resell: { enabled: true },
          recycle: { enabled: true, is_default: true },
        },
      },
      created_at: "2024-03-01T10:00:00Z",
      updated_at: "2024-11-20T14:00:00Z",
    },
  ];
}

export const Route = createFileRoute("/programs")({
  component: ProgramsPage,
});

function ProgramsPage() {
  const { currentTenant } = useTenant();
  const [programs, setPrograms] = useState<Program[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);

  useEffect(() => {
    fetchPrograms();
  }, [currentTenant.id]);

  const fetchPrograms = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `/api/programs?tenantId=${currentTenant.id}`,
      );
      if (response.ok) {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const data = await response.json();
          if (data.programs && data.programs.length > 0) {
            setPrograms(data.programs);
            setLoading(false);
            return;
          }
        }
      }
      // Use tenant-specific demo data
      setPrograms(getTenantDemoPrograms(currentTenant.id));
    } catch (err) {
      console.debug("Failed to load programs, using demo data");
      setPrograms(getTenantDemoPrograms(currentTenant.id));
    } finally {
      setLoading(false);
    }
  };

  const handleProgramCreated = (program: Program) => {
    setPrograms((prev) => [program, ...prev]);
    setShowBuilder(false);
  };

  const handleProgramUpdated = (program: Program) => {
    setPrograms((prev) => prev.map((p) => (p.id === program.id ? program : p)));
    setEditingProgram(null);
    setSelectedProgram(program);
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light" style={{ color: "#171717" }}>
            Programs
          </h1>
          <p className="text-sm mt-1" style={{ color: "#A9A9A9" }}>
            Configure takeback, trade-in, and resale programs with AI-guided
            setup
          </p>
        </div>
        <button
          onClick={() => setShowBuilder(true)}
          className="px-4 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2"
          style={{ backgroundColor: "#00FBA9", color: "#171717" }}
        >
          <PlusIcon />
          New Program
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          label="Total Programs"
          value={programs.length}
          icon={<ProgramIcon />}
        />
        <StatCard
          label="Active"
          value={programs.filter((p) => p.status === "active").length}
          icon={<CheckIcon />}
          highlight
        />
        <StatCard
          label="Draft"
          value={programs.filter((p) => p.status === "draft").length}
          icon={<DraftIcon />}
        />
        <StatCard
          label="Avg Completeness"
          value={
            programs.length > 0
              ? Math.round(
                  programs.reduce((sum, p) => sum + p.completeness, 0) /
                    programs.length,
                ) + "%"
              : "—"
          }
          icon={<ChartIcon />}
        />
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-3 gap-6">
        {/* Programs List */}
        <div className="col-span-2">
          <div
            className="bg-white border overflow-hidden"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <div
              className="px-6 py-4 border-b flex items-center justify-between"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <h2 className="font-medium" style={{ color: "#171717" }}>
                All Programs
              </h2>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 text-xs rounded-full border"
                  style={{
                    borderColor: "rgba(169,169,169,0.2)",
                    color: "#A9A9A9",
                  }}
                >
                  Filter
                </button>
              </div>
            </div>

            {loading ? (
              <div className="p-8 text-center" style={{ color: "#A9A9A9" }}>
                Loading programs...
              </div>
            ) : programs.length === 0 ? (
              <div className="p-12 text-center">
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: "#FAFAFA" }}
                >
                  <ProgramIcon size={32} />
                </div>
                <p className="text-sm mb-2" style={{ color: "#171717" }}>
                  No programs yet
                </p>
                <p className="text-xs mb-6" style={{ color: "#A9A9A9" }}>
                  Create your first program to start accepting trade-ins
                </p>
                <button
                  onClick={() => setShowBuilder(true)}
                  className="px-4 py-2 rounded-full text-sm font-medium"
                  style={{ backgroundColor: "#00FBA9", color: "#171717" }}
                >
                  Create Program
                </button>
              </div>
            ) : (
              <div
                className="divide-y"
                style={{ borderColor: "rgba(169,169,169,0.1)" }}
              >
                {programs.map((program) => (
                  <ProgramRow
                    key={program.id}
                    program={program}
                    isSelected={selectedProgram?.id === program.id}
                    onClick={() => setSelectedProgram(program)}
                    onEdit={() => setEditingProgram(program)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Detail Sidebar */}
        <div className="space-y-4">
          {selectedProgram ? (
            <ProgramDetail
              program={selectedProgram}
              onEdit={() => setEditingProgram(selectedProgram)}
              onActivate={async () => {
                const res = await fetch(
                  `/api/v1/programs/${selectedProgram.id}/activate`,
                  { method: "POST" },
                );
                if (res.ok) {
                  fetchPrograms();
                }
              }}
            />
          ) : (
            <div
              className="bg-white border p-8 text-center"
              style={{ borderColor: "rgba(169,169,169,0.2)" }}
            >
              <div
                className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
                style={{ backgroundColor: "#FAFAFA" }}
              >
                <ProgramIcon />
              </div>
              <p className="text-sm" style={{ color: "#A9A9A9" }}>
                Select a program to view details
              </p>
            </div>
          )}

          {/* Quick Links */}
          <div
            className="bg-white border p-6"
            style={{ borderColor: "rgba(169,169,169,0.2)" }}
          >
            <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
              Quick Actions
            </h3>
            <div className="space-y-2">
              <Link
                to="/knowledge"
                className="flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-gray-50"
                style={{ color: "#171717" }}
              >
                <KnowledgeIcon />
                View in Knowledge Hub
              </Link>
              <Link
                to="/route-tester"
                className="flex items-center gap-3 px-3 py-2 text-sm transition-colors hover:bg-gray-50"
                style={{ color: "#171717" }}
              >
                <TestIcon />
                Test Route API
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Program Builder Modal */}
      {(showBuilder || editingProgram) && (
        <ProgramBuilder
          existingProgram={editingProgram || undefined}
          onClose={() => {
            setShowBuilder(false);
            setEditingProgram(null);
          }}
          onSave={editingProgram ? handleProgramUpdated : handleProgramCreated}
        />
      )}
    </div>
  );
}

// Program Row Component
function ProgramRow({
  program,
  isSelected,
  onClick,
  onEdit,
}: {
  program: Program;
  isSelected: boolean;
  onClick: () => void;
  onEdit: () => void;
}) {
  const { missing } = calculateCompleteness(program.config);

  return (
    <div
      onClick={onClick}
      className="w-full px-6 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors cursor-pointer"
      style={isSelected ? { backgroundColor: "rgba(0,251,169,0.1)" } : {}}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onClick();
      }}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate" style={{ color: "#171717" }}>
            {program.config.name || program.name || "Untitled Program"}
          </p>
          <TypeBadge type={program.type} />
        </div>
        <div className="flex items-center gap-3 mt-1">
          <CompletenessBar value={program.completeness} />
          {missing.length > 0 && (
            <p className="text-xs" style={{ color: "#FF6B6B" }}>
              {missing.length} item{missing.length > 1 ? "s" : ""} missing
            </p>
          )}
        </div>
      </div>
      <StatusBadge status={program.status} />
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        style={{ color: "#A9A9A9" }}
      >
        <EditIcon />
      </button>
    </div>
  );
}

// Completeness Bar
function CompletenessBar({ value }: { value: number }) {
  const color = value >= 80 ? "#00FBA9" : value >= 50 ? "#FFB800" : "#FF6B6B";
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-24 h-1.5 rounded-full overflow-hidden"
        style={{ backgroundColor: "rgba(169,169,169,0.2)" }}
      >
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${value}%`, backgroundColor: color }}
        />
      </div>
      <span className="text-xs font-mono" style={{ color }}>
        {value}%
      </span>
    </div>
  );
}

// Program Detail Panel
function ProgramDetail({
  program,
  onEdit,
  onActivate,
}: {
  program: Program;
  onEdit: () => void;
  onActivate: () => void;
}) {
  const { score, missing } = calculateCompleteness(program.config);
  const config = program.config;

  return (
    <div
      className="bg-white border overflow-hidden"
      style={{ borderColor: "rgba(169,169,169,0.2)" }}
    >
      <div
        className="px-6 py-4 border-b flex items-center justify-between"
        style={{ borderColor: "rgba(169,169,169,0.1)" }}
      >
        <h2 className="font-medium" style={{ color: "#171717" }}>
          Program Details
        </h2>
        <StatusBadge status={program.status} />
      </div>

      <div className="p-6 space-y-5">
        {/* Name & Type */}
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-1"
            style={{ color: "#A9A9A9" }}
          >
            Program
          </p>
          <p className="font-medium" style={{ color: "#171717" }}>
            {config.name || "Untitled"}
          </p>
          <TypeBadge type={program.type} className="mt-1" />
        </div>

        {/* Completeness */}
        <div>
          <p
            className="text-xs uppercase tracking-wider mb-2"
            style={{ color: "#A9A9A9" }}
          >
            Completeness
          </p>
          <CompletenessBar value={score} />
          {missing.length > 0 && (
            <div className="mt-2 space-y-1">
              {missing.map((item) => (
                <p
                  key={item}
                  className="text-xs flex items-center gap-1"
                  style={{ color: "#FF6B6B" }}
                >
                  <span>·</span> {item}
                </p>
              ))}
            </div>
          )}
        </div>

        {/* Collection Methods */}
        {config.collection && (
          <div>
            <p
              className="text-xs uppercase tracking-wider mb-2"
              style={{ color: "#A9A9A9" }}
            >
              Collection
            </p>
            <div className="flex flex-wrap gap-2">
              {config.collection.mail_in?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(0,251,169,0.2)",
                    color: "#00A870",
                  }}
                >
                  Mail-in{" "}
                  {config.collection.mail_in.prepaid_label ? "(prepaid)" : ""}
                </span>
              )}
              {config.collection.in_store_drop_off?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(59,130,246,0.2)",
                    color: "#3B82F6",
                  }}
                >
                  In-store
                </span>
              )}
              {config.collection.courier_pickup?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(168,85,247,0.2)",
                    color: "#A855F7",
                  }}
                >
                  Courier
                </span>
              )}
            </div>
          </div>
        )}

        {/* Valuation */}
        {config.valuation?.method && (
          <div>
            <p
              className="text-xs uppercase tracking-wider mb-2"
              style={{ color: "#A9A9A9" }}
            >
              Valuation
            </p>
            <p className="text-sm" style={{ color: "#171717" }}>
              {config.valuation.method === "fixed" &&
                `Fixed: £${config.valuation.fixed_amount}`}
              {config.valuation.method === "percentage" &&
                `${config.valuation.percentage_of_retail}% of retail`}
              {config.valuation.method === "condition_based" &&
                "Condition-based pricing"}
              {config.valuation.method === "market_based" &&
                "Market-based pricing"}
            </p>
            {config.valuation.method === "condition_based" &&
              config.valuation.condition_multipliers && (
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  {Object.entries(config.valuation.condition_multipliers).map(
                    ([grade, value]) => (
                      <div key={grade} className="flex justify-between">
                        <span style={{ color: "#A9A9A9" }}>{grade}:</span>
                        <span style={{ color: "#171717" }}>
                          {typeof value === "number" && value < 1
                            ? `${value * 100}%`
                            : `£${value}`}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              )}
          </div>
        )}

        {/* Payout */}
        {config.payout && (
          <div>
            <p
              className="text-xs uppercase tracking-wider mb-2"
              style={{ color: "#A9A9A9" }}
            >
              Payout Options
            </p>
            <div className="flex flex-wrap gap-2">
              {config.payout.store_credit?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(0,251,169,0.2)",
                    color: "#00A870",
                  }}
                >
                  Store Credit{" "}
                  {config.payout.store_credit.bonus_percentage
                    ? `(+${config.payout.store_credit.bonus_percentage}%)`
                    : ""}
                </span>
              )}
              {config.payout.cash?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(59,130,246,0.2)",
                    color: "#3B82F6",
                  }}
                >
                  Cash
                </span>
              )}
              {config.payout.gift_card?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(168,85,247,0.2)",
                    color: "#A855F7",
                  }}
                >
                  Gift Card
                </span>
              )}
              {config.payout.charity_donation?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(245,158,11,0.2)",
                    color: "#F59E0B",
                  }}
                >
                  Charity
                </span>
              )}
            </div>
          </div>
        )}

        {/* Routing */}
        {config.routing && (
          <div>
            <p
              className="text-xs uppercase tracking-wider mb-2"
              style={{ color: "#A9A9A9" }}
            >
              Item Routing
            </p>
            <div className="flex flex-wrap gap-2">
              {config.routing.resell?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(0,251,169,0.2)",
                    color: "#00A870",
                  }}
                >
                  Resell
                </span>
              )}
              {config.routing.refurbish?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(59,130,246,0.2)",
                    color: "#3B82F6",
                  }}
                >
                  Refurbish
                </span>
              )}
              {config.routing.donate?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(168,85,247,0.2)",
                    color: "#A855F7",
                  }}
                >
                  Donate{" "}
                  {config.routing.donate.partner
                    ? `(${config.routing.donate.partner})`
                    : ""}
                </span>
              )}
              {config.routing.recycle?.enabled && (
                <span
                  className="px-2 py-1 rounded text-xs"
                  style={{
                    backgroundColor: "rgba(169,169,169,0.2)",
                    color: "#666",
                  }}
                >
                  Recycle {config.routing.recycle.is_default ? "(default)" : ""}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div
          className="pt-4 border-t space-y-2"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          {program.status === "draft" && score >= 80 && (
            <button
              onClick={onActivate}
              className="w-full px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              Activate Program
            </button>
          )}
          {program.status === "draft" && score < 80 && (
            <button
              onClick={onEdit}
              className="w-full px-4 py-2 rounded-full text-sm font-medium"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              Continue Setup
            </button>
          )}
          <button
            onClick={onEdit}
            className="w-full px-4 py-2 rounded-full text-sm font-medium border"
            style={{ borderColor: "rgba(169,169,169,0.3)", color: "#171717" }}
          >
            Edit Program
          </button>
        </div>
      </div>
    </div>
  );
}

// AI-Guided Program Builder
function ProgramBuilder({
  existingProgram,
  onClose,
  onSave,
}: {
  existingProgram?: Program;
  onClose: () => void;
  onSave: (program: Program) => void;
}) {
  const [config, setConfig] = useState<ProgramConfig>(
    existingProgram?.config || {},
  );
  const [programType, setProgramType] = useState<
    "takeback" | "trade_in" | "resale"
  >(existingProgram?.type || "trade_in");
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [currentField, setCurrentField] = useState<string>("name");
  const [saving, setSaving] = useState(false);
  const [editMode] = useState<boolean>(!!existingProgram);
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const chatEndRef = useRef<HTMLDivElement>(null);

  // Initialize chat on mount
  useEffect(() => {
    if (messages.length === 0) {
      if (existingProgram) {
        // Edit mode - show summary and allow section editing
        const { score, missing } = calculateCompleteness(config, programType);
        const summaryMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content:
            score >= 80
              ? `**${existingProgram.config.name || "Your program"}** is ${score}% complete.\n\nClick any section on the right to edit it, or tell me what you'd like to change.`
              : `**${existingProgram.config.name || "Your program"}** needs a few more details (${score}% complete).\n\n**Missing:** ${missing.join(", ")}\n\nClick a section to edit, or I can guide you through what's missing.`,
          suggestions:
            score < 100
              ? ["Complete missing sections", "Edit specific section"]
              : [
                  "Edit program name",
                  "Change payout options",
                  "Update routing",
                ],
          timestamp: new Date(),
        };
        setMessages([summaryMessage]);
      } else {
        // Create mode - guided flow (type-specific)
        const typeLabels = {
          trade_in: "Trade-In",
          takeback: "Takeback",
          resale: "Resale",
        };
        const stepCounts = {
          trade_in: 6,
          takeback: 5,
          resale: 6,
        };
        const initialMessage: ChatMessage = {
          id: crypto.randomUUID(),
          role: "assistant",
          content: `Let's set up your **${typeLabels[programType]}** program. I'll guide you through ${stepCounts[programType]} quick steps.\n\n**Tap a suggestion or type your own answer.**`,
          timestamp: new Date(),
        };
        setMessages([initialMessage]);

        // Start with name
        setTimeout(() => {
          addMessageWithPrompt("name");
        }, 500);
      }
    }
  }, []);

  // Scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const addMessage = (
    role: "user" | "assistant",
    content: string,
    suggestions?: string[],
    field?: string,
    examples?: string,
    tip?: string,
  ) => {
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role,
        content,
        suggestions,
        field,
        examples,
        tip,
        timestamp: new Date(),
      },
    ]);
    if (field) setCurrentField(field);
  };

  // Add a message using the GUIDED_PROMPTS template
  const addMessageWithPrompt = (promptKey: string) => {
    const prompt = GUIDED_PROMPTS[promptKey];
    if (prompt) {
      addMessage(
        "assistant",
        prompt.question,
        prompt.suggestions,
        promptKey,
        prompt.examples,
        prompt.tip,
      );
    }
  };

  // Handle editing a specific section
  const handleEditSection = (sectionId: string) => {
    setEditingSection(sectionId);
    const section = PROGRAM_SECTIONS.find((s) => s.id === sectionId);
    if (section) {
      const firstField = section.fields[0];
      addMessage("assistant", `Let's update the **${section.label}** section.`);
      setTimeout(() => addMessageWithPrompt(firstField), 300);
    }
  };

  const handleUserResponse = (response: string) => {
    addMessage("user", response);
    setInput("");

    // Process response based on current field
    processResponse(response);
  };

  const processResponse = (response: string) => {
    const lowerResponse = response.toLowerCase();

    // Handle edit mode commands
    if (editMode && !editingSection) {
      if (
        lowerResponse.includes("complete") ||
        lowerResponse.includes("missing")
      ) {
        const { missing } = calculateCompleteness(config, programType);
        if (missing.length > 0) {
          // Find first missing section
          const firstMissing = missing[0].toLowerCase();
          if (firstMissing.includes("name")) handleEditSection("basics");
          else if (
            firstMissing.includes("product") ||
            firstMissing.includes("condition")
          )
            handleEditSection("eligibility");
          else if (
            firstMissing.includes("return") ||
            firstMissing.includes("collection")
          )
            handleEditSection("collection");
          else if (
            firstMissing.includes("pay") ||
            firstMissing.includes("valuation")
          )
            handleEditSection("valuation");
          else if (
            firstMissing.includes("payout") ||
            firstMissing.includes("credit")
          )
            handleEditSection("payout");
          else handleEditSection("routing");
        }
        return;
      }
      if (lowerResponse.includes("edit") || lowerResponse.includes("change")) {
        if (lowerResponse.includes("name")) handleEditSection("basics");
        else if (
          lowerResponse.includes("product") ||
          lowerResponse.includes("eligible")
        )
          handleEditSection("eligibility");
        else if (
          lowerResponse.includes("collection") ||
          lowerResponse.includes("return")
        )
          handleEditSection("collection");
        else if (
          lowerResponse.includes("valuation") ||
          lowerResponse.includes("value")
        )
          handleEditSection("valuation");
        else if (
          lowerResponse.includes("payout") ||
          lowerResponse.includes("credit")
        )
          handleEditSection("payout");
        else if (
          lowerResponse.includes("routing") ||
          lowerResponse.includes("route")
        )
          handleEditSection("routing");
        else {
          // No specific section mentioned — ask which one
          addMessage("assistant", "Which section would you like to edit?", [
            "Name",
            "Eligible Products",
            "Conditions",
            "Collection",
            "Valuation",
            "Payout",
            "Item Routing",
          ]);
        }
        return;
      }
      // Direct section name responses (from suggestion buttons)
      const sectionMap: Record<string, string> = {
        name: "basics",
        "eligible products": "eligibility",
        conditions: "eligibility",
        collection: "collection",
        valuation: "valuation",
        payout: "payout",
        "item routing": "routing",
        routing: "routing",
      };
      const matchedSection = sectionMap[lowerResponse];
      if (matchedSection) {
        handleEditSection(matchedSection);
        return;
      }
    }

    switch (currentField) {
      case "name":
        // Validate name - should be short and not a description
        if (response.length > 50 || response.split(" ").length > 6) {
          addMessage(
            "assistant",
            "That looks more like a description. The program name should be short (3-5 words max).\n\n**Try something like:** 'Lululemon Like New' or 'Trade-In Rewards'",
            ["Like New Program", "Trade-In Rewards", "Circular Fashion"],
            "name",
          );
          return;
        }
        setConfig((prev) => ({ ...prev, name: response }));
        // Branch based on program type
        if (programType === "resale") {
          setTimeout(() => addMessageWithPrompt("resale_source"), 300);
        } else {
          setTimeout(() => addMessageWithPrompt("eligible_products"), 300);
        }
        break;

      case "eligible_products":
        if (
          lowerResponse.includes("only") ||
          lowerResponse.includes("our brand")
        ) {
          setConfig((prev) => ({
            ...prev,
            eligible_products: { any_product: false, brands: ["own_brand"] },
          }));
        } else if (
          lowerResponse.includes("any") ||
          lowerResponse.includes("all")
        ) {
          setConfig((prev) => ({
            ...prev,
            eligible_products: { any_product: true },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            eligible_products: { any_product: false, categories: [response] },
          }));
        }
        setTimeout(() => addMessageWithPrompt("conditions"), 300);
        break;

      case "conditions":
        if (
          lowerResponse.includes("like new") ||
          lowerResponse.includes("minimal")
        ) {
          setConfig((prev) => ({
            ...prev,
            conditions: { minimum_grade: "A", accepted_grades: ["A", "B+"] },
          }));
        } else if (
          lowerResponse.includes("good") ||
          lowerResponse.includes("better")
        ) {
          setConfig((prev) => ({
            ...prev,
            conditions: {
              minimum_grade: "B",
              accepted_grades: ["A", "B+", "B", "B-"],
            },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            conditions: {
              minimum_grade: "C",
              accepted_grades: ["A", "B+", "B", "B-", "C+", "C"],
            },
          }));
        }
        // Branch based on program type
        if (programType === "takeback") {
          setTimeout(() => addMessageWithPrompt("collection"), 300);
        } else {
          setTimeout(() => addMessageWithPrompt("collection"), 300);
        }
        break;

      case "collection":
        if (
          lowerResponse.includes("both") ||
          (lowerResponse.includes("mail") && lowerResponse.includes("store"))
        ) {
          setConfig((prev) => ({
            ...prev,
            collection: {
              mail_in: { enabled: true },
              in_store_drop_off: { enabled: true },
            },
          }));
        } else if (lowerResponse.includes("mail")) {
          setConfig((prev) => ({
            ...prev,
            collection: { mail_in: { enabled: true } },
          }));
        } else if (
          lowerResponse.includes("store") ||
          lowerResponse.includes("drop")
        ) {
          setConfig((prev) => ({
            ...prev,
            collection: { in_store_drop_off: { enabled: true } },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            collection: {
              mail_in: { enabled: true },
              in_store_drop_off: { enabled: true },
            },
          }));
        }

        // Follow up about prepaid labels if mail-in, otherwise next question
        if (lowerResponse.includes("mail") || lowerResponse.includes("both")) {
          setTimeout(() => addMessageWithPrompt("mail_prepaid"), 300);
        } else {
          // Branch based on program type
          if (programType === "takeback") {
            setTimeout(() => addMessageWithPrompt("takeback_incentive"), 300);
          } else {
            setTimeout(() => addMessageWithPrompt("valuation"), 300);
          }
        }
        break;

      case "mail_prepaid":
        const prepaid =
          lowerResponse.includes("yes") ||
          lowerResponse.includes("we pay") ||
          lowerResponse.includes("prepaid");
        setConfig((prev) => ({
          ...prev,
          collection: {
            ...prev.collection,
            mail_in: {
              ...prev.collection?.mail_in,
              enabled: true,
              prepaid_label: prepaid,
            },
          },
        }));
        // Branch based on program type - takeback skips valuation
        if (programType === "takeback") {
          setTimeout(() => addMessageWithPrompt("takeback_incentive"), 300);
        } else {
          setTimeout(() => addMessageWithPrompt("valuation"), 300);
        }
        break;

      case "valuation":
        if (lowerResponse.includes("fixed")) {
          setConfig((prev) => ({
            ...prev,
            valuation: { method: "fixed", fixed_amount: 10 },
          }));
          setTimeout(() => {
            addMessage(
              "assistant",
              "What fixed amount should we pay per item?",
              ["£10 per item", "£20 per item", "£30 per item"],
              "valuation_amount",
              "This is what customers receive regardless of item value.",
              "Consider your average item value - most brands set this at 10-20% of average retail price.",
            );
          }, 300);
        } else if (
          lowerResponse.includes("percentage") ||
          lowerResponse.includes("%")
        ) {
          setConfig((prev) => ({
            ...prev,
            valuation: { method: "percentage", percentage_of_retail: 30 },
          }));
          setTimeout(() => {
            addMessage(
              "assistant",
              "What percentage of the original retail price?",
              ["20% of retail", "30% of retail", "40% of retail"],
              "valuation_percentage",
              "E.g., a £100 item at 30% = £30 payout.",
              "Industry standard is 20-40%. Higher % = more volume but lower margins.",
            );
          }, 300);
        } else if (lowerResponse.includes("condition")) {
          setConfig((prev) => ({
            ...prev,
            valuation: { method: "condition_based" },
          }));
          setTimeout(() => addMessageWithPrompt("valuation_condition"), 300);
        } else {
          setConfig((prev) => ({
            ...prev,
            valuation: { method: "market_based" },
          }));
          setTimeout(() => addMessageWithPrompt("payout_method"), 300);
        }
        break;

      case "valuation_amount":
        const amount = parseInt(response.replace(/[^0-9]/g, "")) || 10;
        setConfig((prev) => ({
          ...prev,
          valuation: {
            ...prev.valuation,
            method: "fixed",
            fixed_amount: amount,
          },
        }));
        setTimeout(() => addMessageWithPrompt("payout_method"), 300);
        break;

      case "valuation_percentage":
        const pct = parseInt(response.replace(/[^0-9]/g, "")) || 30;
        setConfig((prev) => ({
          ...prev,
          valuation: {
            ...prev.valuation,
            method: "percentage",
            percentage_of_retail: pct,
          },
        }));
        setTimeout(() => addMessageWithPrompt("payout_method"), 300);
        break;

      case "valuation_condition":
        // Parse condition-based pricing
        const conditionPricing: Record<string, number> = {
          A: 30,
          B: 20,
          C: 10,
        };
        if (response.includes(":")) {
          const parts = response.split(",");
          parts.forEach((part) => {
            const [grade, value] = part.split(":").map((s) => s.trim());
            const num = parseInt(value?.replace(/[^0-9]/g, "") || "0");
            if (grade && num) {
              // Map friendly names to grades
              if (grade.toLowerCase().includes("excellent") || grade === "A")
                conditionPricing.A = num;
              else if (grade.toLowerCase().includes("good") || grade === "B")
                conditionPricing.B = num;
              else if (grade.toLowerCase().includes("fair") || grade === "C")
                conditionPricing.C = num;
            }
          });
        } else if (response.includes("%")) {
          // Percentage based
          const nums = response.match(/\d+/g)?.map(Number) || [50, 30, 10];
          conditionPricing.A = (nums[0] || 50) / 100;
          conditionPricing.B = (nums[1] || 30) / 100;
          conditionPricing.C = (nums[2] || 10) / 100;
        }
        setConfig((prev) => ({
          ...prev,
          valuation: {
            method: "condition_based",
            condition_multipliers: conditionPricing,
          },
        }));
        setTimeout(() => addMessageWithPrompt("payout_method"), 300);
        break;

      case "payout_method":
        const payout: ProgramConfig["payout"] = {};

        // Check for "all options" or multiple selections
        const wantsAll =
          lowerResponse.includes("all") ||
          lowerResponse.includes("every") ||
          lowerResponse.includes("these options");
        const wantsCredit =
          lowerResponse.includes("credit") || lowerResponse.includes("store");
        const wantsCash =
          lowerResponse.includes("cash") ||
          lowerResponse.includes("paypal") ||
          lowerResponse.includes("refund");
        const wantsGift = lowerResponse.includes("gift");
        const wantsCharity =
          lowerResponse.includes("charity") || lowerResponse.includes("donate");

        if (wantsAll) {
          // Enable all payout options
          payout.store_credit = { enabled: true };
          payout.cash = { enabled: true };
          payout.gift_card = { enabled: true };
          payout.charity_donation = { enabled: true };
          setConfig((prev) => ({ ...prev, payout }));
          setTimeout(() => addMessageWithPrompt("store_credit_bonus"), 300);
        } else if (wantsCredit && wantsCash) {
          // Multiple options selected
          payout.store_credit = { enabled: true };
          payout.cash = { enabled: true };
          setConfig((prev) => ({ ...prev, payout }));
          setTimeout(() => addMessageWithPrompt("store_credit_bonus"), 300);
        } else if (wantsCredit) {
          payout.store_credit = { enabled: true };
          setTimeout(() => addMessageWithPrompt("store_credit_bonus"), 300);
        } else if (wantsCash) {
          payout.cash = { enabled: true };
          setConfig((prev) => ({ ...prev, payout }));
          setTimeout(() => addMessageWithPrompt("routing"), 300);
        } else if (wantsGift) {
          payout.gift_card = { enabled: true };
          setConfig((prev) => ({ ...prev, payout }));
          setTimeout(() => addMessageWithPrompt("routing"), 300);
        } else if (wantsCharity) {
          payout.charity_donation = { enabled: true };
          setConfig((prev) => ({ ...prev, payout }));
          setTimeout(() => addMessageWithPrompt("routing"), 300);
        } else {
          // Check for custom percentage/value payout descriptions
          const percentMatch = lowerResponse.match(/(\d+)\s*%/);
          if (percentMatch) {
            const percentage = parseInt(percentMatch[1], 10);
            // User specified a percentage-based payout — set as cash with percentage of retail
            payout.cash = { enabled: true };
            setConfig((prev) => ({
              ...prev,
              payout,
              valuation: {
                ...prev.valuation,
                method: "percentage",
                percentage_of_retail: percentage,
              },
            }));
            addMessage(
              "assistant",
              `Got it — **${percentage}% of retail value** paid out as cash. Would you also like to offer store credit as an alternative?`,
              ["Yes, add store credit", "No, just cash"],
              "payout_add_credit",
            );
          } else {
            // Default to store credit if nothing matched
            payout.store_credit = { enabled: true };
            addMessage(
              "assistant",
              "I'll set **store credit** as the payout method. You can always add more options later.\n\nWould you like to offer a bonus for choosing store credit?",
              ["+20% bonus", "+10% bonus", "No bonus"],
              "store_credit_bonus",
            );
          }
        }
        if (!wantsAll && !wantsCredit) {
          setConfig((prev) => ({ ...prev, payout }));
        }
        break;

      case "store_credit_bonus":
        const bonus = lowerResponse.includes("20")
          ? 20
          : lowerResponse.includes("10")
            ? 10
            : 0;
        setConfig((prev) => ({
          ...prev,
          payout: {
            ...prev.payout,
            store_credit: { enabled: true, bonus_percentage: bonus },
          },
        }));
        // Now ask about routing (final step!)
        setTimeout(() => addMessageWithPrompt("routing"), 300);
        break;

      case "payout_add_credit":
        if (lowerResponse.includes("yes") || lowerResponse.includes("add")) {
          setConfig((prev) => ({
            ...prev,
            payout: {
              ...prev.payout,
              store_credit: { enabled: true },
            },
          }));
        }
        setTimeout(() => addMessageWithPrompt("routing"), 300);
        break;

      case "routing":
        const routing: ProgramConfig["routing"] = {};
        let routingUnderstood = true;

        if (
          lowerResponse.includes("resell") &&
          lowerResponse.includes("refurbish")
        ) {
          // Both resell and refurbish
          routing.resell = { enabled: true, conditions: ["A", "B+"] };
          routing.refurbish = { enabled: true, conditions: ["B", "B-"] };
          routing.recycle = { enabled: true, is_default: true };
        } else if (
          lowerResponse.includes("resell") &&
          !lowerResponse.includes("refurbish")
        ) {
          routing.resell = { enabled: true, conditions: ["A", "B+", "B"] };
          routing.recycle = { enabled: true, is_default: true };
        } else if (lowerResponse.includes("refurbish")) {
          routing.refurbish = { enabled: true };
          routing.resell = { enabled: true };
          routing.recycle = { enabled: true, is_default: true };
        } else if (
          lowerResponse.includes("recycle") &&
          !lowerResponse.includes("resell")
        ) {
          routing.recycle = { enabled: true, is_default: true };
        } else if (
          lowerResponse.includes("donate") ||
          lowerResponse.includes("charity")
        ) {
          routing.donate = { enabled: true };
          routing.recycle = { enabled: true, is_default: true };
        } else if (
          lowerResponse.includes("mix") ||
          lowerResponse.includes("all") ||
          lowerResponse.includes("everything")
        ) {
          routing.resell = { enabled: true, conditions: ["A", "B+"] };
          routing.refurbish = { enabled: true, conditions: ["B", "B-"] };
          routing.donate = { enabled: true };
          routing.recycle = { enabled: true, is_default: true };
        } else {
          // Didn't understand - ask again
          routingUnderstood = false;
          addMessage(
            "assistant",
            "I didn't catch that. What should happen to items after you collect them?\n\n**Choose one:**",
            [
              "Resell good items",
              "Refurbish then resell",
              "Recycle everything",
              "Donate to charity",
              "Mix of all options",
            ],
            "routing",
            "• **Resell** - Sell good condition items on your site\n• **Refurbish** - Repair items before selling\n• **Recycle** - Material recovery\n• **Donate** - Give to charity partners",
            "Most brands choose 'Resell good items' with recycling as the fallback.",
          );
        }

        if (routingUnderstood) {
          setConfig((prev) => ({ ...prev, routing }));
          // Go to priority order
          setTimeout(() => addMessageWithPrompt("routing_priority"), 300);
        }
        break;

      case "routing_resell":
        if (
          lowerResponse.includes("excellent") ||
          lowerResponse.includes("good only")
        ) {
          setConfig((prev) => ({
            ...prev,
            routing: {
              ...prev.routing,
              resell: { enabled: true, conditions: ["A", "B+"] },
            },
          }));
        } else if (
          lowerResponse.includes("all") ||
          lowerResponse.includes("wearable")
        ) {
          setConfig((prev) => ({
            ...prev,
            routing: {
              ...prev.routing,
              resell: { enabled: true, conditions: ["A", "B+", "B", "B-"] },
            },
          }));
        } else if (lowerResponse.includes("refurbish")) {
          setConfig((prev) => ({
            ...prev,
            routing: {
              ...prev.routing,
              resell: { enabled: true },
              refurbish: { enabled: true },
            },
          }));
        }
        setTimeout(() => {
          addMessage(
            "assistant",
            GUIDED_PROMPTS.routing_priority.question,
            GUIDED_PROMPTS.routing_priority.suggestions,
            "routing_priority",
          );
        }, 300);
        break;

      case "routing_donate":
        if (lowerResponse.includes("local")) {
          setConfig((prev) => ({
            ...prev,
            routing: {
              ...prev.routing,
              donate: { enabled: true, partner: "Local charities" },
            },
          }));
        } else if (
          lowerResponse.includes("partner") ||
          lowerResponse.includes("specific")
        ) {
          setConfig((prev) => ({
            ...prev,
            routing: {
              ...prev.routing,
              donate: { enabled: true, partner: "Partner charity" },
            },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            routing: { ...prev.routing, donate: { enabled: false } },
          }));
        }
        setTimeout(() => addMessageWithPrompt("routing_priority"), 300);
        break;

      case "routing_priority":
        const priorityOrder: string[] = [];
        if (
          lowerResponse.includes("resell") &&
          lowerResponse.includes("refurbish")
        ) {
          priorityOrder.push("resell", "refurbish", "recycle");
        } else if (
          lowerResponse.includes("resell") &&
          lowerResponse.includes("donate")
        ) {
          priorityOrder.push("resell", "donate", "recycle");
        } else if (
          lowerResponse.includes("refurbish") &&
          lowerResponse.includes("resell")
        ) {
          priorityOrder.push("refurbish", "resell", "recycle");
        } else {
          priorityOrder.push("resell", "refurbish", "donate", "recycle");
        }
        setConfig((prev) => ({
          ...prev,
          routing: {
            ...prev.routing,
            priority_order: priorityOrder,
          } as ProgramConfig["routing"],
        }));
        finishSetup();
        break;

      // ============ TAKEBACK-SPECIFIC CASES ============
      case "takeback_incentive":
        if (
          lowerResponse.includes("voucher") ||
          lowerResponse.includes("discount")
        ) {
          setConfig((prev) => ({
            ...prev,
            takeback: {
              ...prev.takeback,
              incentive: "voucher",
              voucher_amount: 5,
            },
          }));
        } else if (lowerResponse.includes("credit")) {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, incentive: "store_credit" },
          }));
        } else if (
          lowerResponse.includes("charity") ||
          lowerResponse.includes("donate")
        ) {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, incentive: "charity_donation" },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, incentive: "none" },
          }));
        }
        setTimeout(() => addMessageWithPrompt("takeback_destination"), 300);
        break;

      case "takeback_destination":
        if (lowerResponse.includes("recycle")) {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, destination: "recycle" },
          }));
        } else if (
          lowerResponse.includes("donate") ||
          lowerResponse.includes("charity")
        ) {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, destination: "donate" },
          }));
        } else if (lowerResponse.includes("refurbish")) {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, destination: "refurbish_donate" },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            takeback: { ...prev.takeback, destination: "mix" },
          }));
        }
        finishSetup();
        break;

      // ============ RESALE-SPECIFIC CASES ============
      case "resale_source":
        if (lowerResponse.includes("trade") || lowerResponse.includes("our")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, source: "trade_in" },
          }));
        } else if (lowerResponse.includes("consign")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, source: "consignment" },
          }));
        } else if (
          lowerResponse.includes("external") ||
          lowerResponse.includes("source")
        ) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, source: "external" },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, source: "mixed" },
          }));
        }
        setTimeout(() => addMessageWithPrompt("resale_conditions"), 300);
        break;

      case "resale_conditions":
        if (
          lowerResponse.includes("like new") ||
          lowerResponse.includes("new only")
        ) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, quality_threshold: "A" },
          }));
        } else if (
          lowerResponse.includes("good") ||
          lowerResponse.includes("better")
        ) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, quality_threshold: "B" },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, quality_threshold: "C" },
          }));
        }
        setTimeout(() => addMessageWithPrompt("resale_channels"), 300);
        break;

      case "resale_channels":
        const channels: string[] = [];
        if (
          lowerResponse.includes("website") ||
          lowerResponse.includes("our site")
        )
          channels.push("website");
        if (lowerResponse.includes("telegram")) channels.push("telegram");
        if (
          lowerResponse.includes("marketplace") ||
          lowerResponse.includes("ebay") ||
          lowerResponse.includes("depop")
        )
          channels.push("marketplaces");
        if (lowerResponse.includes("all") || channels.length === 0) {
          channels.push("website", "telegram", "marketplaces");
        }
        setConfig((prev) => ({
          ...prev,
          resale: { ...prev.resale, channels },
        }));
        setTimeout(() => addMessageWithPrompt("resale_pricing"), 300);
        break;

      case "resale_pricing":
        if (
          lowerResponse.includes("fixed") ||
          lowerResponse.includes("%") ||
          lowerResponse.includes("retail")
        ) {
          setConfig((prev) => ({
            ...prev,
            resale: {
              ...prev.resale,
              pricing_strategy: "percentage",
              percentage: 50,
            },
          }));
        } else if (lowerResponse.includes("market")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, pricing_strategy: "market_based" },
          }));
        } else if (lowerResponse.includes("manual")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, pricing_strategy: "manual" },
          }));
        } else if (lowerResponse.includes("auction")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, pricing_strategy: "auction" },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, pricing_strategy: "market_based" },
          }));
        }
        setTimeout(() => addMessageWithPrompt("resale_fulfillment"), 300);
        break;

      case "resale_fulfillment":
        if (
          lowerResponse.includes("direct") ||
          lowerResponse.includes("we ship")
        ) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, fulfillment: "direct" },
          }));
        } else if (lowerResponse.includes("pickup")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, fulfillment: "pickup" },
          }));
        } else if (lowerResponse.includes("drop")) {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, fulfillment: "dropship" },
          }));
        } else {
          setConfig((prev) => ({
            ...prev,
            resale: { ...prev.resale, fulfillment: "mixed" },
          }));
        }
        finishSetup();
        break;

      default:
        // Handle free-form input - try to understand intent
        if (editingSection) {
          setEditingSection(null);
          addMessage(
            "assistant",
            "Got it! Section updated. What else would you like to change, or click **Save Program** when ready.",
            ["Edit another section", "Save program"],
            "edit_complete",
          );
        } else {
          finishSetup();
        }
    }
  };

  const finishSetup = () => {
    const { score, missing } = calculateCompleteness(config, programType);
    setEditingSection(null);
    setTimeout(() => {
      if (score >= 80) {
        addMessage(
          "assistant",
          `**All done!** Your program is ${score}% complete and ready to go.\n\nReview the configuration on the right, then click **Save Program** to create it.`,
          ["Save program", "Edit something"],
          "complete",
        );
      } else {
        addMessage(
          "assistant",
          `We're at ${score}% complete. ${missing.length > 0 ? `We still need to define: ${missing.join(", ")}.` : ""} You can save as draft and continue later, or keep configuring.`,
          ["Save as draft", "Keep configuring"],
          "incomplete",
        );
      }
    }, 300);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { score } = calculateCompleteness(config, programType);
      const url = existingProgram
        ? `/api/v1/programs/${existingProgram.id}`
        : "/api/v1/programs";

      const response = await fetch(url, {
        method: existingProgram ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: config.name || "Untitled Program",
          type: programType,
          config,
          completeness: score,
          status: score >= 80 ? "draft" : "draft", // Always draft until manually activated
        }),
      });

      if (response.ok) {
        const data = await response.json();
        onSave(data.program);
      }
    } catch (err) {
      console.error("Failed to save program");
    } finally {
      setSaving(false);
    }
  };

  const { score, missing } = calculateCompleteness(config, programType);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col"
        style={{ boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)" }}
      >
        {/* Header */}
        <div
          className="px-6 py-4 border-b flex items-center justify-between"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <div>
            <h2 className="text-lg font-medium" style={{ color: "#171717" }}>
              {existingProgram ? "Edit Program" : "New Program"}
            </h2>
            <div className="flex items-center gap-3 mt-1">
              <CompletenessBar value={score} />
              <span className="text-xs" style={{ color: "#A9A9A9" }}>
                {missing.length === 0
                  ? "Ready to activate"
                  : `${missing.length} items remaining`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Program Type Selector */}
            <select
              value={programType}
              onChange={(e) =>
                setProgramType(e.target.value as typeof programType)
              }
              className="px-3 py-1.5 border rounded-full text-sm"
              style={{ borderColor: "rgba(169,169,169,0.3)" }}
            >
              <option value="trade_in">Trade-in</option>
              <option value="takeback">Takeback</option>
              <option value="resale">Resale</option>
            </select>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100"
              style={{ color: "#A9A9A9" }}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Main Content - Split View */}
        <div className="flex-1 flex overflow-hidden">
          {/* Chat Panel */}
          <div
            className="flex-1 flex flex-col border-r"
            style={{ borderColor: "rgba(169,169,169,0.1)" }}
          >
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] px-4 py-3 rounded-2xl ${msg.role === "user" ? "rounded-br-md" : "rounded-bl-md"}`}
                    style={{
                      backgroundColor:
                        msg.role === "user" ? "#171717" : "#FAFAFA",
                      color: msg.role === "user" ? "#FFFFFF" : "#171717",
                    }}
                  >
                    <p
                      className="text-sm whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{
                        __html: msg.content.replace(
                          /\*\*(.*?)\*\*/g,
                          '<strong style="color: #00A870">$1</strong>',
                        ),
                      }}
                    />

                    {/* Examples - collapsible */}
                    {msg.examples && (
                      <div
                        className="mt-3 p-3 text-xs"
                        style={{ backgroundColor: "rgba(0,251,169,0.08)" }}
                      >
                        <p
                          className="whitespace-pre-wrap"
                          style={{ color: "#666" }}
                          dangerouslySetInnerHTML={{
                            __html: msg.examples.replace(
                              /\*\*(.*?)\*\*/g,
                              "<strong>$1</strong>",
                            ),
                          }}
                        />
                      </div>
                    )}

                    {/* Tip */}
                    {msg.tip && (
                      <p
                        className="mt-2 text-xs italic"
                        style={{ color: "#888" }}
                      >
                        Tip: {msg.tip}
                      </p>
                    )}

                    {msg.suggestions && (
                      <div className="mt-3">
                        <div className="flex flex-wrap gap-2">
                          {msg.suggestions.map((suggestion) => (
                            <button
                              key={suggestion}
                              onClick={() => handleUserResponse(suggestion)}
                              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all hover:scale-105 hover:shadow-sm"
                              style={{
                                backgroundColor: "#FFFFFF",
                                border: "1px solid rgba(0,251,169,0.5)",
                                color: "#171717",
                              }}
                            >
                              {suggestion}
                            </button>
                          ))}
                        </div>
                        <p
                          className="text-xs mt-2"
                          style={{ color: "#A9A9A9" }}
                        >
                          Click a suggestion or type your own answer
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>

            {/* Input */}
            <div
              className="p-4 border-t"
              style={{ borderColor: "rgba(169,169,169,0.1)" }}
            >
              <div className="flex gap-2">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    input.trim() &&
                    handleUserResponse(input)
                  }
                  placeholder="Type your answer here or click a suggestion above..."
                  className="flex-1 px-4 py-2 border text-sm focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none transition-all"
                  style={{ borderColor: "rgba(169,169,169,0.3)" }}
                />
                <button
                  onClick={() => input.trim() && handleUserResponse(input)}
                  disabled={!input.trim()}
                  className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: "#171717", color: "#FFFFFF" }}
                >
                  Send
                </button>
              </div>
            </div>
          </div>

          {/* Config Preview Panel */}
          <div className="w-80 bg-gray-50 overflow-y-auto p-6">
            <h3 className="font-medium mb-4" style={{ color: "#171717" }}>
              Configuration
            </h3>

            <div className="space-y-4">
              <ConfigSection
                title="Name"
                value={config.name}
                onClick={() => handleEditSection("basics")}
                isEditing={editingSection === "basics"}
              />

              <ConfigSection
                title="Eligible Products"
                onClick={() => handleEditSection("eligibility")}
                isEditing={editingSection === "eligibility"}
                value={
                  config.eligible_products?.any_product
                    ? "Any product"
                    : config.eligible_products?.brands?.join(", ") ||
                      config.eligible_products?.categories?.join(", ")
                }
              />

              <ConfigSection
                title="Conditions"
                onClick={() => handleEditSection("eligibility")}
                isEditing={editingSection === "eligibility"}
                value={
                  config.conditions?.accepted_grades?.length
                    ? `Grades: ${config.conditions.accepted_grades.join(", ")}`
                    : undefined
                }
              />

              <ConfigSection
                title="Collection"
                onClick={() => handleEditSection("collection")}
                isEditing={editingSection === "collection"}
                value={
                  [
                    config.collection?.mail_in?.enabled && "Mail-in",
                    config.collection?.in_store_drop_off?.enabled && "In-store",
                    config.collection?.courier_pickup?.enabled && "Courier",
                  ]
                    .filter(Boolean)
                    .join(", ") || undefined
                }
              />

              <ConfigSection
                title="Valuation"
                onClick={() => handleEditSection("valuation")}
                isEditing={editingSection === "valuation"}
                value={
                  config.valuation?.method
                    ? `${config.valuation.method}${
                        config.valuation.fixed_amount
                          ? `: £${config.valuation.fixed_amount}`
                          : config.valuation.percentage_of_retail
                            ? `: ${config.valuation.percentage_of_retail}%`
                            : ""
                      }`
                    : undefined
                }
              />

              <ConfigSection
                title="Payout"
                onClick={() => handleEditSection("payout")}
                isEditing={editingSection === "payout"}
                value={
                  [
                    config.payout?.store_credit?.enabled &&
                      `Store credit${config.payout.store_credit.bonus_percentage ? ` (+${config.payout.store_credit.bonus_percentage}%)` : ""}`,
                    config.payout?.cash?.enabled && "Cash",
                    config.payout?.gift_card?.enabled && "Gift card",
                    config.payout?.charity_donation?.enabled && "Charity",
                  ]
                    .filter(Boolean)
                    .join(", ") || undefined
                }
              />

              <ConfigSection
                title="Item Routing"
                onClick={() => handleEditSection("routing")}
                isEditing={editingSection === "routing"}
                value={
                  [
                    config.routing?.resell?.enabled && "Resell",
                    config.routing?.refurbish?.enabled && "Refurbish",
                    config.routing?.donate?.enabled && "Donate",
                    config.routing?.recycle?.enabled && "Recycle",
                  ]
                    .filter(Boolean)
                    .join(" → ") || undefined
                }
              />
            </div>

            {/* Missing Items */}
            {missing.length > 0 && (
              <div
                className="mt-6 p-3"
                style={{ backgroundColor: "rgba(255,107,107,0.1)" }}
              >
                <p
                  className="text-xs font-medium mb-2"
                  style={{ color: "#FF6B6B" }}
                >
                  Missing
                </p>
                {missing.map((item) => (
                  <p
                    key={item}
                    className="text-xs"
                    style={{ color: "#FF6B6B" }}
                  >
                    · {item}
                  </p>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-6 py-4 border-t flex items-center justify-between"
          style={{ borderColor: "rgba(169,169,169,0.1)" }}
        >
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-full text-sm"
            style={{ color: "#A9A9A9" }}
          >
            Cancel
          </button>
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving || !config.name}
              className="px-4 py-2 rounded-full text-sm font-medium disabled:opacity-50"
              style={{ backgroundColor: "#00FBA9", color: "#171717" }}
            >
              {saving ? "Saving..." : "Save Program"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Config Section in Preview
function ConfigSection({
  title,
  value,
  onClick,
  isEditing,
}: {
  title: string;
  value?: string | null;
  onClick?: () => void;
  isEditing?: boolean;
}) {
  const hasValue = value && value !== "Not set";
  return (
    <div
      onClick={onClick}
      className={`p-2 -mx-2 rounded-lg transition-colors ${onClick ? "cursor-pointer hover:bg-gray-100" : ""}`}
      style={isEditing ? { backgroundColor: "rgba(0,251,169,0.1)" } : {}}
    >
      <div className="flex items-center justify-between">
        <p
          className="text-xs uppercase tracking-wider"
          style={{ color: "#A9A9A9" }}
        >
          {title}
        </p>
        {hasValue ? (
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: "#00FBA9" }}
          />
        ) : (
          <span
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: "#FFB800" }}
          />
        )}
      </div>
      {hasValue ? (
        <p className="text-sm mt-1" style={{ color: "#171717" }}>
          {value}
        </p>
      ) : (
        <p className="text-sm mt-1 italic" style={{ color: "#D1D1D1" }}>
          Not set
        </p>
      )}
    </div>
  );
}

// Badges
function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, { bg: string; text: string }> = {
    active: { bg: "rgba(0,251,169,0.2)", text: "#00A870" },
    draft: { bg: "rgba(169,169,169,0.2)", text: "#666" },
    archived: { bg: "rgba(169,169,169,0.1)", text: "#999" },
  };
  const style = colors[status] || colors.draft;

  return (
    <span
      className="px-2 py-1 rounded text-xs font-medium capitalize"
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {status}
    </span>
  );
}

function TypeBadge({
  type,
  className = "",
}: {
  type: string;
  className?: string;
}) {
  const colors: Record<string, { bg: string; text: string }> = {
    takeback: { bg: "rgba(0,251,169,0.2)", text: "#00A870" },
    trade_in: { bg: "rgba(59,130,246,0.2)", text: "#3B82F6" },
    resale: { bg: "rgba(168,85,247,0.2)", text: "#A855F7" },
  };
  const safeType = type || "trade_in";
  const style = colors[safeType] || colors.trade_in;
  const label =
    safeType === "trade_in"
      ? "Trade-in"
      : safeType.charAt(0).toUpperCase() + safeType.slice(1);

  return (
    <span
      className={`px-2 py-0.5 rounded text-xs font-medium ${className}`}
      style={{ backgroundColor: style.bg, color: style.text }}
    >
      {label}
    </span>
  );
}

// Stat Card
function StatCard({
  label,
  value,
  icon,
  highlight = false,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  highlight?: boolean;
}) {
  return (
    <div
      className="bg-white border p-5"
      style={{
        borderColor: highlight ? "#00FBA9" : "rgba(169,169,169,0.2)",
        borderWidth: highlight ? 2 : 1,
      }}
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-2xl font-light" style={{ color: "#171717" }}>
            {value}
          </p>
          <p className="text-xs mt-1" style={{ color: "#A9A9A9" }}>
            {label}
          </p>
        </div>
        <div style={{ color: highlight ? "#00FBA9" : "#A9A9A9" }}>{icon}</div>
      </div>
    </div>
  );
}

// Icons
function ProgramIcon({ size = 20 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9 5H7C5.89543 5 5 5.89543 5 7V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V7C19 5.89543 18.1046 5 17 5H15M9 5C9 6.10457 9.89543 7 11 7H13C14.1046 7 15 6.10457 15 5M9 5C9 3.89543 9.89543 3 11 3H13C14.1046 3 15 3.89543 15 5M12 12H15M12 16H15M9 12H9.01M9 16H9.01"
        strokeLinecap="round"
      />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M12 5V19M5 12H19" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function DraftIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M11 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V13M18.5 2.5C19.3284 1.67157 20.6716 1.67157 21.5 2.5C22.3284 3.32843 22.3284 4.67157 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChartIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M16 12L12 8L8 12M12 8V16M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function EditIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M11 4H6C4.89543 4 4 4.89543 4 6V18C4 19.1046 4.89543 20 6 20H18C19.1046 20 20 19.1046 20 18V13M18.5 2.5C19.3284 1.67157 20.6716 1.67157 21.5 2.5C22.3284 3.32843 22.3284 4.67157 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M6 18L18 6M6 6L18 18"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function KnowledgeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TestIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <path
        d="M9.75 17L9 20L12 19L15 20L14.25 17M3 13H7M17 13H21M12 3V7M5.63604 5.63604L8.46447 8.46447M15.5355 8.46447L18.364 5.63604M8 14C8 16.2091 9.79086 18 12 18C14.2091 18 16 16.2091 16 14C16 11.7909 14.2091 10 12 10C9.79086 10 8 11.7909 8 14Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
